import { Fragment, type ReactNode } from 'react';
import { useLoaderData } from 'react-router';

import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { useEnquiryVisibility } from '~/modules/enquiry/hooks/use-enquiry-visibility';
import { useVariantOptionState } from '@modules/product/context/VariantOptionContext';
import { getProductRangeUrl } from '~/modules/product/util/get-product-range-url';
import { ARCHIPRO_SPEC_SHEET_ID } from '~/modules/product/tear-sheet/archipro-spec-sheet';
import type { loader } from '~/routes/_app.product.$urlSegment';
import {
    CONTENT_CAP_PADDED,
    NumberedAccordion,
    TrackedWebsiteCta,
} from '~/ui-rocco/pages/detail-shared';
import { ACTION_CARDS_HEADING_CLASS } from '~/ui-rocco/pages/detail-shared/DetailActionCards';
import { useProductEnquiryModal } from '../hooks/use-product-enquiry-modal';
import { useProductPrice } from './purchase/use-product-price';
import { useSizesInUse } from './purchase/purchase-flow-parts';
import { groupApprovedFacetOptions } from './spec-accordion/group-facet-options';

/** An answer's type: the project About's prose, on the page's ink. */
const ANSWER_CLASS =
    'm-0 max-w-[50rem] font-sans leading-[1.4] font-normal text-[#323232] max-md:text-base md:text-[1.25rem]';

/** A link inside an answer, underlined so it reads as one in running text. */
const LINK_CLASS =
    'text-[#10100f] underline decoration-black/30 underline-offset-4 transition-colors hover:decoration-black';

/** The enquiry, as a link in the sentence rather than a button beside it. */
const INLINE_ACTION_CLASS = cn(
    LINK_CLASS,
    'cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[length:inherit] leading-[inherit]',
);

/** How many of the tagged projects an answer names before it stops. */
const PROJECTS_NAMED = 3;

interface Faq {
    /**
     * How much the question is worth when only a few are shown: 1 first.
     * Who supplies it and how to reach them come first, then what it costs,
     * where it has been used and what it comes in.
     */
    rank: number;
    question: string;
    answer: ReactNode;
}

/** How many questions the section shows. */
const FAQ_LIMIT = 5;

/**
 * The best `limit` questions by rank, kept in the order they read on the
 * page rather than the order they rank in.
 */
const topFaqs = (faqs: Faq[], limit: number): Faq[] => {
    const kept = new Set(
        [...faqs].sort((a, b) => a.rank - b.rank).slice(0, limit),
    );
    return faqs.filter((faq) => kept.has(faq));
};

/**
 * Who designed it and whose brand it is, said once when they are the same
 * company.
 */
const designerLine = (
    title: string,
    designer?: string | null,
    brand?: string | null,
): string => {
    if (designer && brand && designer.trim() === brand.trim()) {
        return `The ${title} is designed and made by ${designer}`;
    }
    if (designer && brand) {
        return `The ${title} was designed by ${designer} and is made under the ${brand} brand`;
    }
    if (designer) return `The ${title} was designed by ${designer}`;
    return `The ${title} is made by ${brand ?? ''}`;
};

/** A file category mid-sentence: lower case, but CAD and BIM keep their capitals. */
const sentenceCase = (name: string): string =>
    name
        .split(' ')
        .map((word) =>
            word.length > 1 && word === word.toUpperCase()
                ? word
                : word.toLowerCase(),
        )
        .join(' ');

/** "A, B and C", with the items as they come — text or links. */
const joinList = (items: ReactNode[]): ReactNode =>
    items.map((item, index) => (
        // The list's items are rendered in place; their position is their
        // identity within one sentence.
        // eslint-disable-next-line react/no-array-index-key
        <Fragment key={index}>
            {index === 0 ? null : index === items.length - 1 ? ' and ' : ', '}
            {item}
        </Fragment>
    ));

/**
 * Frequently asked questions about the product, the five best of those it
 * can answer, each answered from what the page already knows: who supplies it, what it costs, what it comes in, where
 * it is made, who installs it and where it has been used. A question only
 * appears when its answer is on record, so nothing here is a guess.
 *
 * The answers are written to send the reader on: the supplier's profile,
 * their products and contact pages, their website, the category and range,
 * and the projects the product has been specified in.
 */
const useProductFaqs = (): Faq[] => {
    const { product, featuredProjects } = useLoaderData<typeof loader>();
    const { label: priceLabel, isPriceRange } = useProductPrice();
    const { attributeMap } = useVariantOptionState();
    const sizesInUse = useSizesInUse();
    const { openEnquiryModal } = useProductEnquiryModal();
    const { showEnquiryCTA } = useEnquiryVisibility(product.Professional);

    const title = product.Title;
    const professional = product.Professional;
    const supplier = professional.Title;
    const website = product.Website || professional.Website;
    const category = product.Categories?.[0];
    const rangeTitle = product.LibraryRange?.Title?.trim();
    const facets = groupApprovedFacetOptions(product.FacetOptions ?? []);
    const origin = facets.find((facet) =>
        /country of origin/i.test(facet.Label),
    )?.options;

    const profileLink = (
        <RoccoLink to={professional.Link} className={LINK_CLASS}>
            {supplier}
        </RoccoLink>
    );
    const websiteLink = website ? (
        <TrackedWebsiteCta
            asLink
            website={website}
            libraryItemID={product.ID}
            professionalID={professional.ID}
            professionalTitle={supplier}
            source="ProductFaq"
            className={LINK_CLASS}
        >
            their website
        </TrackedWebsiteCta>
    ) : null;
    const enquire = (label: string) =>
        showEnquiryCTA ? (
            <button
                type="button"
                onClick={() => openEnquiryModal()}
                className={INLINE_ACTION_CLASS}
            >
                {label}
            </button>
        ) : (
            <RoccoLink
                to={`${professional.Link}/contact`}
                className={LINK_CLASS}
            >
                {label}
            </RoccoLink>
        );

    const faqs: Faq[] = [];

    faqs.push({
        rank: 1,
        question: `Who supplies the ${title}?`,
        answer: (
            <>
                The {title} is supplied by {profileLink}.{' '}
                {websiteLink ? (
                    <>It is also on {websiteLink}, and you can browse </>
                ) : (
                    'You can browse '
                )}
                <RoccoLink
                    to={`${professional.Link}/our-products`}
                    className={LINK_CLASS}
                >
                    every product {supplier} lists on ArchiPro
                </RoccoLink>
                .
            </>
        ),
    });

    faqs.push({
        rank: 3,
        question: `How much does the ${title} cost?`,
        answer: priceLabel ? (
            <>
                {supplier} lists the {title}{' '}
                {/^from\s/i.test(priceLabel)
                    ? `from ${priceLabel.replace(/^from\s+/i, '')}`
                    : `at ${priceLabel}`}
                {isPriceRange ? ', as a price indication' : ''}. For an exact
                price for your project, {enquire('send them an enquiry')}.
            </>
        ) : (
            <>
                {supplier} does not publish a price for the {title} on ArchiPro.{' '}
                {enquire('Send them an enquiry')} for pricing, or visit{' '}
                {websiteLink ?? profileLink}.
            </>
        ),
    });

    const options = [...attributeMap.values()]
        .map((attribute) => ({
            name: attribute.Name,
            labels: [
                ...new Set(
                    attribute.options
                        .map((option) => option.Label?.trim())
                        .filter((label): label is string => !!label),
                ),
            ],
        }))
        .filter((attribute) => attribute.name && attribute.labels.length > 1);
    // Each attribute once: a supplier can define the same group twice.
    const seenNames = new Set<string>();
    const distinctOptions = options.filter((attribute) => {
        const key = attribute.name.toLowerCase();
        if (seenNames.has(key)) return false;
        seenNames.add(key);
        return true;
    });
    if (distinctOptions.length > 0) {
        faqs.push({
            rank: 5,
            question: `What options does the ${title} come in?`,
            answer: (
                <>
                    It comes in{' '}
                    {joinList(
                        distinctOptions.map(
                            (attribute) =>
                                `${attribute.labels.length} ${attribute.name.toLowerCase()} options (${attribute.labels.join(', ')})`,
                        ),
                    )}
                    . Pick one in the row under the description to see it
                    {priceLabel ? ' and its price' : ''}.
                </>
            ),
        });
    }

    if (sizesInUse.length > 1) {
        faqs.push({
            rank: 6,
            question: `What sizes is the ${title} available in?`,
            answer: (
                <>
                    It comes in {sizesInUse.length} sizes:{' '}
                    {joinList(sizesInUse.map((size) => size.FullTitle))}. For a
                    size that is not listed, {enquire(`ask ${supplier}`)}.
                </>
            ),
        });
    }

    if (category?.PrettyTitle) {
        faqs.push({
            rank: 8,
            question: `What kind of product is the ${title}?`,
            answer: (
                <>
                    It is listed under{' '}
                    {category.Link ? (
                        <RoccoLink to={category.Link} className={LINK_CLASS}>
                            {category.PrettyTitle}
                        </RoccoLink>
                    ) : (
                        category.PrettyTitle
                    )}{' '}
                    on ArchiPro, where you can compare it with{' '}
                    {category.PrettyTitle.toLowerCase()} from other suppliers.
                </>
            ),
        });
    }

    if (rangeTitle) {
        faqs.push({
            rank: 7,
            question: `Is the ${title} part of a range?`,
            answer: (
                <>
                    Yes. It is part of the{' '}
                    <RoccoLink
                        to={getProductRangeUrl(professional, rangeTitle)}
                        className={LINK_CLASS}
                    >
                        {rangeTitle}
                    </RoccoLink>{' '}
                    range from {profileLink}, where you can see every product in
                    the range together.
                </>
            ),
        });
    }

    // A brand that is only the supplier's own name says nothing the first
    // answer has not. Trimmed first, or a CMS field holding nothing but
    // whitespace reads as a value and the sentence names no one.
    const brandName = product.Brand?.trim();
    const brand = brandName && brandName !== supplier.trim() ? brandName : null;
    const designer = product.Designer?.trim() || null;
    if (designer || brand) {
        faqs.push({
            rank: 12,
            question: designer
                ? `Who designed the ${title}?`
                : `Who makes the ${title}?`,
            answer: (
                <>
                    {designerLine(title, designer, brand)}. It is supplied
                    through {profileLink}.
                </>
            ),
        });
    }

    if (origin?.length) {
        faqs.push({
            rank: 11,
            question: `Where is the ${title} made?`,
            answer: (
                <>
                    Its country of origin is {joinList(origin)}, as listed by{' '}
                    {profileLink}. {enquire('Ask them')} about lead times to
                    your site.
                </>
            ),
        });
    }

    if (product.WarrantyDuration) {
        faqs.push({
            rank: 10,
            question: `What warranty does the ${title} come with?`,
            answer: (
                <>
                    {supplier} lists a warranty of {product.WarrantyDuration}.
                    For the full terms, {enquire('get in touch with them')} or
                    see {websiteLink ?? profileLink}.
                </>
            ),
        });
    }

    if (product.SamplesAvailable && showEnquiryCTA) {
        faqs.push({
            rank: 14,
            question: `Can I get a sample of the ${title}?`,
            answer: (
                <>
                    Yes, {supplier} offers samples.{' '}
                    {enquire('Request one with an enquiry')} and say which
                    option you would like to see.
                </>
            ),
        });
    }

    const downloads = (product.LibraryDownloads ?? []).filter(
        (download) =>
            download.ID !== ARCHIPRO_SPEC_SHEET_ID &&
            (download.InternalLink || download.ExternalLink),
    );
    const hasSpecSheet = (product.LibraryDownloads ?? []).some(
        (download) => download.ID === ARCHIPRO_SPEC_SHEET_ID,
    );
    if (downloads.length > 0) {
        const names = [
            ...new Set(
                downloads
                    .map((download) =>
                        (
                            download.FileCategory?.Title ??
                            download.Title ??
                            ''
                        ).trim(),
                    )
                    .filter(Boolean),
            ),
        ];
        faqs.push({
            rank: 9,
            question: `Are there specifications and files for the ${title}?`,
            answer: (
                <>
                    Yes. {supplier} provides{' '}
                    {names.length > 0
                        ? joinList(names.map(sentenceCase))
                        : 'files'}{' '}
                    for it, in the Downloads section above
                    {hasSpecSheet ? ', alongside an ArchiPro spec sheet' : ''}.
                    More of their files are on{' '}
                    <RoccoLink
                        to={`${professional.Link}/downloads`}
                        className={LINK_CLASS}
                    >
                        their downloads page
                    </RoccoLink>
                    .
                </>
            ),
        });
    }

    const installers = product.Installers?.professionals ?? [];
    if (installers.length > 0) {
        faqs.push({
            rank: 13,
            question: `Who can install the ${title}?`,
            answer: (
                <>
                    {/* "Including" only where some are left unnamed: the
                        sentence names the first three, so two or three
                        installers are all of them. */}
                    {installers.length === 1
                        ? 'One installer is listed for it on ArchiPro: '
                        : `${installers.length} installers are listed for it on ArchiPro${
                              installers.length > PROJECTS_NAMED
                                  ? ', including '
                                  : ': '
                          }`}
                    {joinList(
                        installers.slice(0, PROJECTS_NAMED).map((installer) => (
                            <RoccoLink
                                key={installer.link}
                                to={installer.link}
                                className={LINK_CLASS}
                            >
                                {installer.title}
                            </RoccoLink>
                        )),
                    )}
                    .
                </>
            ),
        });
    }

    const projects = featuredProjects.FeaturedProjects.edges
        .map((edge) => edge.node)
        .filter((project) => project.title && project.link);
    if (projects.length > 0) {
        faqs.push({
            rank: 4,
            question: `Has the ${title} been used in any projects?`,
            answer: (
                <>
                    Yes. It has been specified in projects on ArchiPro,
                    including{' '}
                    {joinList(
                        projects.slice(0, PROJECTS_NAMED).map((project) => (
                            <>
                                <RoccoLink
                                    to={project.link}
                                    className={LINK_CLASS}
                                >
                                    {project.title}
                                </RoccoLink>
                                {project.professional?.name &&
                                project.professional.link ? (
                                    <>
                                        {' '}
                                        by{' '}
                                        <RoccoLink
                                            to={project.professional.link}
                                            className={LINK_CLASS}
                                        >
                                            {project.professional.name}
                                        </RoccoLink>
                                    </>
                                ) : null}
                            </>
                        )),
                    )}
                    . See how it was used in a finished space.
                </>
            ),
        });
    }

    faqs.push({
        rank: 2,
        question: `How do I get in touch with ${supplier}?`,
        answer: (
            <>
                {enquire('Send them an enquiry')} from this page, see their{' '}
                <RoccoLink
                    to={`${professional.Link}/contact`}
                    className={LINK_CLASS}
                >
                    contact details on ArchiPro
                </RoccoLink>
                {websiteLink ? <>, or visit {websiteLink}</> : null}. The{' '}
                {profileLink} profile on ArchiPro has the rest of their
                products.
            </>
        ),
    });

    return topFaqs(faqs, FAQ_LIMIT);
};

/**
 * Frequently asked questions, above More products: the page's numbered
 * accordion under the same heading setting as Get more Specific, each row a
 * question and its answer.
 */
export const ProductFaqSection = () => {
    const faqs = useProductFaqs();
    if (faqs.length === 0) return null;

    // Every answer stays in the page while its row is shut, so the answers
    // and their links are in the served HTML rather than only the open one.
    const rows = faqs.map((faq) => ({
        title: faq.question,
        forceMount: true,
        content: (
            <div className="max-md:pt-3 max-md:pb-5 md:pt-[1.5rem] md:pb-[2.5rem]">
                <p className={ANSWER_CLASS}>{faq.answer}</p>
            </div>
        ),
    }));

    return (
        // 60px on top of the bottom padding the section above keeps, so the
        // heading stands the 240px under it that Get more Specific's does.
        // Nothing below: More products owns the air after the last row.
        <section className="w-full bg-[#f5f5f7] max-md:pt-8 max-md:pb-0 md:pt-[3.75rem] md:pb-0">
            <div className="max-md:px-6 md:px-[3.125rem]">
                <div className={CONTENT_CAP_PADDED}>
                    <h2
                        className={cn(
                            ACTION_CARDS_HEADING_CLASS,
                            'text-[#181818]',
                        )}
                    >
                        Frequently asked questions
                    </h2>
                </div>
            </div>
            <NumberedAccordion
                className="bg-[#f5f5f7] max-md:mt-6 max-md:px-5 md:mt-[3.125rem] md:px-[3rem]"
                headingLevel={3}
                rows={rows}
                multiple
                defaultOpenTitles={rows[0] ? [rows[0].title] : undefined}
            />
        </section>
    );
};
